import { useState } from 'react';
import type { FormEvent } from 'react';
import { Button, Field, TextInput } from '@salonomy/ui';
import { useConfirmPasswordReset } from '../data/auth-recovery.js';
import { ApiClientError } from '../lib/api.js';

/**
 * Password reset confirmation (pre-session). Reads the emailed token from `?token=`, collects a new
 * password, and POSTs /v1/auth/password/reset-confirm. This endpoint does NOT auto-login — on success
 * we show a confirmation and send the user back to sign in (where they authenticate with the new
 * password).
 *
 * A missing token is caught before any request (so we never POST an empty token); the API's 400 for
 * an invalid / expired / already-used reset link flows through ApiClientError into the inline error
 * slot, matching the Login form's treatment.
 */
export function ResetPasswordPage() {
  const confirm = useConfirmPasswordReset();
  const token = new URLSearchParams(window.location.search).get('token') ?? '';
  const [password, setPassword] = useState('');
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!token) {
      setError('This reset link is missing its token. Request a new one.');
      return;
    }
    try {
      await confirm.mutateAsync({ token, password });
      setDone(true);
    } catch (err) {
      setError(
        err instanceof ApiClientError
          ? err.message
          : 'Could not reset your password. Check your connection.',
      );
    }
  }

  return (
    <div className="login">
      <div className="login__card">
        <div className="login__mark">
          Salonomy<span style={{ color: 'var(--slm-cobalt)' }}>.</span>
        </div>
        <div className="login__sub">Choose a new password.</div>
        {done ? (
          <div className="login__form">
            <div className="login__sub">Your password has been reset. You can now sign in.</div>
            <a
              href={import.meta.env.BASE_URL}
              style={{ fontSize: 13, fontWeight: 600, color: 'var(--slm-cobalt)' }}
            >
              Go to sign in →
            </a>
          </div>
        ) : token ? (
          <form className="login__form" onSubmit={onSubmit}>
            {error ? <div className="login__error">{error}</div> : null}
            <Field label="New password" htmlFor="password">
              <TextInput
                id="password"
                type="password"
                autoComplete="new-password"
                required
                autoFocus
                minLength={10}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 10 characters"
              />
            </Field>
            <Button type="submit" size="lg" block disabled={confirm.isPending}>
              {confirm.isPending ? 'Saving…' : 'Set new password'}
            </Button>
          </form>
        ) : (
          <div className="login__form">
            <div className="login__error">
              This reset link is missing its token. Request a new one.
            </div>
            <a
              href={import.meta.env.BASE_URL}
              style={{ fontSize: 13, fontWeight: 600, color: 'var(--slm-cobalt)' }}
            >
              Back to sign in →
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
